<template>
  <div v-if="isdisplay" class="pae-overlay" @click.self="close">
    <div class="pae-dialog">

      <!-- Header -->
      <div class="pae-head">
        <div class="pae-head-left">
          <div class="pae-head-icon"><i class="ri-tv-2-line"></i></div>
          <div class="pae-head-txt">
            <span>Autoriser un écran</span>
            <small>Poste de production - cuisine ou bar</small>
          </div>
        </div>
        <button class="pae-close" @click="close" aria-label="Fermer">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <div class="pae-body">

        <div v-if="erreur" class="pae-err">
          <i class="ri-error-warning-line"></i><span>{{ erreur }}</span>
        </div>

        <!-- Places restantes : le caissier doit le savoir AVANT de demander un
             code, pas au moment du refus. -->
        <div v-if="quota && quota.max > 0" class="pae-quota"
             :class="{ plein: quota.utilises >= quota.max }">
          <i class="ri-tv-2-line"></i>
          <span>{{ quota.utilises }} écran(s) sur {{ quota.max }} autorisé(s)</span>
          <em v-if="quota.utilises >= quota.max">plafond atteint</em>
        </div>

        <!-- ── Choix du poste ── -->
        <label class="pae-lbl">Poste à rattacher</label>
        <div class="pae-postes">
          <button v-for="p in postes" :key="p.code"
                  :class="['pae-poste', { on: poste === p.code }]"
                  :style="{ '--c': p.couleur }"
                  @click="poste = p.code">
            <i :class="p.icone"></i>
            <span>{{ p.libelle }}</span>
            <em>{{ p.categories.join(', ') }}</em>
          </button>
        </div>

        <label class="pae-lbl">Nom de l'écran <small>(facultatif)</small></label>
        <input v-model="libelle" class="pae-input" maxlength="60"
               placeholder="Passe chaud, Bar terrasse…" />

        <!-- ── Le code ── -->
        <div v-if="code" class="pae-code-box">
          <span class="pae-code-l">Code à saisir sur l'écran</span>
          <strong class="pae-code">{{ codeEspace }}</strong>
          <span class="pae-code-exp" :class="{ 'exp-fin': resteCode <= 60 }">
            <i class="ri-timer-line"></i>
            <template v-if="resteCode > 0">Valable encore {{ mmss(resteCode) }}</template>
            <template v-else>Code expiré - générez-en un nouveau</template>
          </span>
          <span class="pae-code-aide">
            Sur l'écran : menu <em>CUISINE</em>, puis saisissez ce code.
          </span>
        </div>

        <button class="pae-btn pae-btn-pri" @click="genererCode" :disabled="chargement">
          <i :class="chargement ? 'ri-loader-4-line pae-spin' : 'ri-key-2-line'"></i>
          {{ code ? 'Générer un nouveau code' : 'Générer le code' }}
        </button>

        <!-- ── Écrans rattachés ── -->
        <div class="pae-sep">
          <span>Écrans rattachés à cette caisse</span>
          <button class="pae-refresh" @click="rafraichir" title="Rafraîchir">
            <i class="ri-refresh-line"></i>
          </button>
        </div>

        <div v-if="ecrans.length === 0" class="pae-vide">
          <i class="ri-tv-2-line"></i>
          <span>Aucun écran rattaché pour l'instant.</span>
        </div>

        <div v-for="e in ecrans" :key="e.id" class="pae-ecran">
          <span class="pae-dot" :class="e.connecte ? 'dot-on' : (e.codeEnAttente ? 'dot-wait' : 'dot-off')"></span>
          <div class="pae-ecran-info">
            <span class="pae-ecran-nom">
              {{ e.libelle || e.posteLibelle }}
              <em class="pae-ecran-poste">{{ e.posteLibelle }}</em>
            </span>
            <span class="pae-ecran-etat">
              <template v-if="e.connecte">En ligne</template>
              <template v-else-if="e.codeEnAttente">
                En attente de saisie du code ({{ mmss(e.codeSecondesRestantes) }})
              </template>
              <template v-else>Hors ligne<template v-if="e.lastSeenAt"> · vu {{ dateCourte(e.lastSeenAt) }}</template></template>
              <template v-if="e.jetonExpireAt && !e.codeEnAttente">
                · jeton jusqu'au {{ dateCourte(e.jetonExpireAt) }}
              </template>
            </span>
          </div>
          <button class="pae-kick" @click="revoquer(e)" title="Débrancher cet écran">
            <i class="ri-link-unlink"></i>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
/**
 * Autoriser un écran de production (cuisine / bar).
 *
 * La caisse émet un code court ; l'écran l'échange contre un jeton durable.
 * Aucun QR ici, volontairement : un écran de cuisine est un poste FIXE, souvent
 * sans caméra. Un code de six chiffres se recopie mieux qu'une URL.
 *
 * Routes appelées (toutes protégées, le btkpid vient de la session) :
 *   .../screen.../caisse/.../cuisine/code
 *   (la présence n'est plus appelée en HTTP : elle arrive du parent, via le socket)
 *   .../screen.../caisse/.../cuisine/revoquer
 *   .../screen.../ecran/.../cuisine/parametrage
 */
const axios = require("axios");
import * as JwtService from "@/common/jwt.service";
import { randomString } from "@/allfunction";

const BASE = "/egl745874pscreen771714dfe";

export default {
    name: "modalautoriserecran",

    /* La liste des écrans vient du parent (caisse), alimentée par le WEBSOCKET.
       La modale ne sonde plus la présence en HTTP : elle affiche `ecrans` et
       demande un rafraîchissement (@refresh) après une action. */
    props: {
        ecrans: { type: Array, default: () => [] },
        /* { max, utilises } venu du même événement de présence que la liste :
           aucun appel supplémentaire. Null tant que rien n'est arrivé. */
        quota: { type: Object, default: null },
    },

    data: () => ({
        isdisplay: false,
        pidcaisse: "",

        postes: [],
        poste: "cuisine",
        libelle: "",

        code: "",
        resteCode: 0,

        chargement: false,
        erreur: "",
    }),

    computed: {
        /* Groupé par trois : un code lu à voix haute à travers une salle passe
           beaucoup mieux « 482 917 » que « 482917 ». */
        codeEspace() {
            const c = String(this.code || "");
            return c.length === 6 ? c.slice(0, 3) + " " + c.slice(3) : c;
        },

        /* Nom lisible de la caisse en cours. Le backend ne connaît que le pid
           (une centaine de caractères) : c'est la caisse qui lui apporte le nom,
           pour que l'écran de cuisine affiche « Caisse 1 » et non le pid. */
        nomCaisse() {
            const $c = (this.$store.state.listeallcaisses || [])
                .find((x) => x && String(x.pid) === this.pidcaisse);
            return $c ? String($c.name || "") : "";
        },
    },

    methods: {
        show(pidcaisse) {
            this.pidcaisse = String(pidcaisse || "");
            this.erreur = "";
            this.code = "";
            this.resteCode = 0;
            this.isdisplay = true;
            this.chargerParametrage();
            /* Décompte local du code (aucun réseau). La présence, elle, arrive
               par le socket via le parent. */
            this._tick = setInterval(() => {
                if (this.resteCode > 0) this.resteCode--;
            }, 1000);
        },

        close() {
            this.isdisplay = false;
            clearInterval(this._tick);
        },

        _headers() {
            return {
                "X-Requested-With": "XMLHttpRequest",
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: "Bearer " + JwtService.getToken(),
                csrftoken: this.$store.state.appsociete,
            };
        },

        _url(chemin) {
            return process.env.VUE_APP_API_URL + BASE + chemin + "/" + randomString(120);
        },

        async chargerParametrage() {
            try {
                const { data } = await axios.post(
                    this._url("/ecran/othetdskd8755dsdhisd/cuisine/parametrage"),
                    {}, { headers: this._headers(), withCredentials: true }
                );
                if (data.success && data.m) this.postes = data.m.postes || [];
            } catch (e) {
                /* Le paramétrage n'est qu'un confort d'affichage : sans lui on
                   garde le poste par défaut plutôt que de bloquer la modale. */
                console.warn("[ecran] parametrage indisponible", e);
            }
        },

        async genererCode() {
            if (!this.pidcaisse) { this.erreur = "Aucune caisse en cours."; return; }
            this.erreur = "";
            this.chargement = true;
            try {
                const { data } = await axios.post(
                    this._url("/caisse/othetdskd8755dsdhisd/cuisine/code"),
                    {
                        pidcaisse: this.pidcaisse,
                        namecaisse: this.nomCaisse,
                        poste: this.poste,
                        libelle: this.libelle,
                        emisPar: this.$store.state.appusername || "",
                        meta: { origine: "caisse" },
                    },
                    { headers: this._headers(), withCredentials: true }
                );
                if (!data.success) {
                    this.erreur = this.messageRefus(data.m && data.m.code);
                    return;
                }
                this.code = data.m.code;
                this.resteCode = data.m.expireDansSecondes || 0;
                this.rafraichir();
            } catch (e) {
                this.erreur = "Le code n'a pas pu être généré. Réessayez.";
                console.error("[ecran] code", e);
            } finally {
                this.chargement = false;
            }
        },

        /* Demande au parent (caisse) de rafraîchir la présence PAR LE SOCKET.
           Plus aucun appel HTTP de présence ici. */
        rafraichir() {
            this.$emit("refresh");
        },

        async revoquer(e) {
            try {
                const { data } = await axios.post(
                    this._url("/caisse/othetdskd8755dsdhisd/cuisine/revoquer"),
                    { id: e.id },
                    { headers: this._headers(), withCredentials: true }
                );
                if (!data.success) {
                    this.erreur = this.messageRefus(data.m && data.m.code);
                    return;
                }
                toastr["success"]("Écran débranché.", "<b></b>");
                this.rafraichir();
            } catch (err) {
                this.erreur = "La révocation a échoué.";
                console.error("[ecran] revoquer", err);
            }
        },

        /* Les codes du backend sont faits pour les logs ; on n'affiche jamais
           leur message brut au caissier. */
        messageRefus(code) {
            const M = {
                SESSION_INVALID: "Session expirée. Reconnectez-vous.",
                PARAMS_INVALID: "Requête incomplète. Actualisez la page.",
                POSTE_INVALID: "Ce poste n'est pas reconnu.",
                CODE_INDISPONIBLE: "Impossible d'attribuer un code. Réessayez.",
                ECRAN_INTROUVABLE: "Cet écran n'existe plus.",
                /* Quota d'écrans. Le second message dit COMMENT s'en sortir :
                   la cause la plus fréquente est un écran resté « autorisé »
                   après un ré-appairage, qui occupe une place pour rien. */
                SCREEN_NON_LICENCIE: "Votre licence ne couvre pas les écrans de production.",
                SCREEN_QUOTA_ATTEINT: this.quota
                    ? "Plafond atteint : " + this.quota.utilises + " écran(s) sur "
                      + this.quota.max + " autorisé(s). Débranchez un écran pour en autoriser un autre."
                    : "Plafond d'écrans atteint. Débranchez un écran pour en autoriser un autre.",
            };
            return M[code] || "Opération impossible pour le moment.";
        },

        mmss(s) {
            const v = Math.max(0, parseInt(s, 10) || 0);
            return String(Math.floor(v / 60)).padStart(2, "0") + ":" + String(v % 60).padStart(2, "0");
        },

        dateCourte(v) {
            if (!v) return "-";
            const d = new Date(v);
            if (isNaN(d.getTime())) return "-";
            return d.toLocaleString("fr-FR", {
                day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
            });
        },
    },

    beforeUnmount() {
        clearInterval(this._tick);
    },
};
</script>

<style scoped>
/* Compteur de places d'écrans, au-dessus du choix de poste. */
.pae-quota{display:flex;align-items:center;gap:8px;margin:0 0 12px;padding:7px 10px;
           border-radius:8px;font-size:12.5px;background:rgba(59,130,246,.10);
           border:1px solid rgba(59,130,246,.35);color:#93c5fd}
.pae-quota.plein{background:rgba(245,158,11,.12);border-color:rgba(245,158,11,.45);color:#fbbf24}
.pae-quota em{margin-left:auto;font-style:normal;font-weight:700;font-size:11px;text-transform:uppercase}

.pae-overlay {
  position: fixed; inset: 0; z-index: 10030;
  background: rgba(0, 0, 0, .55);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

.pae-dialog {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; width: min(480px, 100%); max-height: 92vh;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, .45);
  animation: pae-in .2s cubic-bezier(.4, 0, .2, 1);
}
@keyframes pae-in {
  from { opacity: 0; transform: scale(.93) translateY(10px); }
  to   { opacity: 1; transform: none; }
}

.pae-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.pae-head-left { display: flex; align-items: center; gap: 10px; }
.pae-head-icon {
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f97316, #ef4444); color: #fff; font-size: 1.05rem;
}
.pae-head-txt { display: flex; flex-direction: column; }
.pae-head-txt span { font-weight: 600; font-size: .93rem; color: var(--text); }
.pae-head-txt small { font-size: .74rem; color: var(--text2); }
.pae-close {
  width: 32px; height: 32px; border-radius: 8px; cursor: pointer;
  border: 1px solid var(--border); background: transparent; color: var(--text2);
}
.pae-close:hover { color: #ef4444; border-color: #ef4444; }

.pae-body { padding: 16px 18px; overflow-y: auto; display: flex; flex-direction: column; gap: 10px; }

.pae-err {
  display: flex; align-items: flex-start; gap: 8px;
  background: rgba(239, 68, 68, .12); border: 1px solid rgba(239, 68, 68, .35);
  border-radius: 9px; padding: 10px 12px; font-size: .79rem; color: #dc2626;
}

.pae-lbl { font-size: .77rem; font-weight: 600; color: var(--text2); }
.pae-lbl small { font-weight: 400; opacity: .8; }

.pae-postes { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.pae-poste {
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  padding: 12px 8px; border-radius: 11px; cursor: pointer;
  border: 1px solid var(--border); background: var(--surface2, var(--sunken, transparent));
  color: var(--text2);
}
.pae-poste i { font-size: 1.35rem; }
.pae-poste span { font-size: .84rem; font-weight: 600; }
.pae-poste em { font-style: normal; font-size: .67rem; opacity: .75; }
.pae-poste.on { border-color: var(--c); color: var(--c); box-shadow: inset 0 0 0 1px var(--c); }

.pae-input {
  padding: 9px 12px; border-radius: 9px; font-size: .84rem;
  border: 1px solid var(--border); background: transparent; color: var(--text);
}

.pae-code-box {
  display: flex; flex-direction: column; align-items: center; gap: 5px;
  padding: 16px; border-radius: 12px;
  border: 1px dashed var(--border); background: rgba(99, 102, 241, .07);
}
.pae-code-l { font-size: .73rem; color: var(--text2); }
.pae-code {
  font-size: 2.5rem; font-weight: 800; letter-spacing: .12em;
  color: var(--text); font-variant-numeric: tabular-nums; line-height: 1.1;
}
.pae-code-exp {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: .76rem; font-weight: 600; color: #059669;
}
.pae-code-exp.exp-fin { color: #dc2626; }
.pae-code-aide { font-size: .71rem; color: var(--text2); text-align: center; }
.pae-code-aide em { font-style: normal; font-weight: 700; }

.pae-sep {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 6px; padding-top: 10px; border-top: 1px solid var(--border);
  font-size: .77rem; font-weight: 600; color: var(--text2);
}
.pae-refresh {
  width: 28px; height: 28px; border-radius: 7px; cursor: pointer;
  border: 1px solid var(--border); background: transparent; color: var(--text2);
}

.pae-vide {
  display: flex; flex-direction: column; align-items: center; gap: 5px;
  padding: 18px 0; color: var(--text2); opacity: .6; font-size: .77rem;
}
.pae-vide i { font-size: 1.5rem; }

.pae-ecran {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 11px; border-radius: 9px;
  border: 1px solid var(--border); background: transparent;
}
.pae-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; background: #94a3b8; }
.dot-on { background: #10b981; box-shadow: 0 0 0 3px rgba(16, 185, 129, .22); }
.dot-wait { background: #f59e0b; }
.dot-off { background: #94a3b8; }
.pae-ecran-info { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.pae-ecran-nom { font-size: .83rem; font-weight: 600; color: var(--text); }
.pae-ecran-poste {
  font-style: normal; font-size: .67rem; font-weight: 500; color: var(--text2);
  border: 1px solid var(--border); border-radius: 5px; padding: 1px 5px; margin-left: 5px;
}
.pae-ecran-etat { font-size: .71rem; color: var(--text2); }
.pae-kick {
  width: 30px; height: 30px; border-radius: 7px; cursor: pointer;
  border: 1px solid var(--border); background: transparent; color: var(--text2);
}
.pae-kick:hover { color: #ef4444; border-color: #ef4444; }

.pae-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  padding: 10px 16px; border-radius: 9px; cursor: pointer;
  font-size: .82rem; font-weight: 600;
  border: 1px solid var(--border); background: transparent; color: var(--text);
}
.pae-btn:disabled { opacity: .5; cursor: not-allowed; }
.pae-btn-pri { background: #6366f1; border-color: #6366f1; color: #fff; }
.pae-btn-pri:hover:not(:disabled) { filter: brightness(1.08); }

.pae-spin { animation: pae-rot 1s linear infinite; display: inline-block; }
@keyframes pae-rot { to { transform: rotate(360deg); } }
</style>
